import { Container, Row, Col } from "react-bootstrap";
import ProjectCard from "./ProjectCards";
import Particle from "../Particle";
import { Helmet } from "react-helmet-async";

const clientProjects = [
  {
    domain: "Insurance · Remote (New York, USA)",
    title: "Enterprise AWS Standardization & FinOps Framework",
    bullets: [
      "Standardised AWS infrastructure through reusable Terraform modules & Terragrunt with org-wide tagging and cost-allocation metadata built into provisioning",
      "Automated resource tagging at scale using Python & AWS APIs for consistent ownership, application, environment & client attribution",
      "Built an application-level FinOps framework using AWS Cost Categories with rule-based cost pillars for transparent spend visibility",
      "Eliminated cloud waste across unused, stale & over-provisioned workloads; implemented Savings Plans and Reserved Instances",
    ],
    tags: ["AWS", "Terraform", "Terragrunt", "GitHub Actions", "Python", "Cost Explorer", "CUR", "Cost Categories", "GitHub Copilot"],
  },
  {
    domain: "Web Platform · Remote (Coplay, USA)",
    title: "AWS Migration, Hardening & CI/CD for Web Applications",
    bullets: [
      "Migrated a production WordPress application from WP Admin hosting to AWS EC2 with VPC, Security Groups, ALB, WAF, SSL/TLS & Nginx",
      "Provisioned AWS infrastructure for a React, Node.js, MySQL & Redis application across dev and deployment environments",
      "Implemented Jenkins CI/CD with SonarQube, Trivy & Gitleaks security gates for repeatable, validated deployments",
      "Hardened public-facing applications with ALB, WAF, Security Groups, SSL/TLS & Nginx; added Prometheus/Grafana and CloudWatch endpoint monitoring",
    ],
    tags: ["EC2", "VPC", "ALB", "WAF", "Jenkins", "Docker", "Nginx", "Prometheus", "Grafana", "React", "Node.js", "MySQL", "Redis"],
  },
  {
    domain: "FinTech Product · Onsite (Nagpur, India)",
    title: "Financial Application Platform — Infrastructure & Data Automation",
    bullets: [
      "Provisioned secure AWS infrastructure for a React + Python financial application, with PostgreSQL on Amazon RDS and a scalable S3 + CloudFront frontend",
      "Migrated production PostgreSQL from an EC2-hosted instance to Amazon RDS with zero downtime",
      "Automated deployments with Jenkins & Docker for containerized backend services on EC2",
      "Implemented cron-based data ingestion workflows to fetch, validate & sync external datasets, with Prometheus/Grafana/CloudWatch monitoring",
    ],
    tags: ["EC2", "S3", "CloudFront", "RDS", "WAF", "ALB", "EventBridge", "Docker", "Jenkins", "React", "Python", "FastAPI", "PostgreSQL"],
  },
  {
    domain: "FinTech · Remote (Mumbai, India)",
    title: "Multi-Server AWS Environments for Java, Python & React Apps",
    bullets: [
      "Architected multi-server AWS environments for Java, Python, React & WordPress applications with containerized workloads",
      "Configured S3-based hosting, ALB, WAF, SSL/TLS and network-level security controls for isolated, production-ready deployments",
      "Automated CI/CD, backups & observability with Jenkins, Docker, Prometheus, Grafana, Uptime Kuma & CloudWatch",
    ],
    tags: ["EC2", "S3", "CloudFront", "RDS", "WAF", "ALB", "Jenkins", "Docker", "Prometheus", "Grafana", "Uptime Kuma", "Java", "Python", "React"],
  },
  {
    domain: "Investment Advisory · Nagpur, India",
    title: "Monolith-to-Multi-Tier AWS Re-Architecture",
    bullets: [
      "Re-architected a monolithic investment advisory application into a multi-tier AWS architecture for scalability & availability",
      "Implemented private-subnet networking & IAM permission boundaries to strengthen infrastructure security",
      "Automated deployments with Jenkins & GitLab CI/CD",
    ],
    tags: ["EC2", "RDS", "S3", "CloudFront", "VPC", "IAM", "Jenkins", "GitLab", "Prometheus", "Grafana"],
  },
  {
    domain: "Wildlife Conservation · Remote (Mumbai, India)",
    title: "Security Incident Response & Resilience Hardening",
    bullets: [
      "Responded to a production code-injection incident — isolated the affected environment and executed secure recovery procedures",
      "Implemented Lambda-based automation, GuardDuty threat detection & S3 lifecycle-managed backups",
      "Strengthened security, operational resilience & infrastructure cost efficiency post-incident",
    ],
    tags: ["EC2", "S3", "CloudFront", "Lambda", "ECR", "WAF", "GuardDuty", "IAM", "Backup", "Python"],
  },
  {
    domain: "Capital Markets · Remote (Mumbai, India)",
    title: "Production AWS Infrastructure & Access Governance",
    bullets: [
      "Designed & provisioned scalable AWS infrastructure with networking, load balancing & security controls for production workloads",
      "Implemented IAM lifecycle governance and EventBridge-based EC2 scheduling for operational efficiency",
      "Built Windows EC2 recovery workflows to strengthen infrastructure resilience",
    ],
    tags: ["EC2", "RDS", "IAM", "EventBridge", "Lambda", "ALB", "S3", "GitLab", "Windows Server"],
  },
];

const internalProjects = [
  {
    domain: "Cloud Infrastructure · Hybrid Cloud",
    title: "7-Node Proxmox Cluster & Internal AWS Platform",
    bullets: [
      "Built a 7-node Proxmox VE cluster on existing office hardware — a secure hybrid-cloud platform for internal development, testing & infrastructure operations at zero added cloud spend",
      "Integrated GajShield firewall for controlled, secure internet-facing application exposure",
      "Extended hybrid connectivity into the AWS VPC via Site-to-Site VPN",
    ],
    tags: ["Proxmox VE", "Ubuntu", "GajShield", "AWS VPC", "Site-to-Site VPN"],
  },
  {
    domain: "DevOps · Platform Engineering",
    title: "Self-Hosted Enterprise GitLab Platform",
    bullets: [
      "Architected and self-hosted an enterprise GitLab environment, eliminating dependency on third-party version-control licensing",
      "Integrated source control with CI/CD, code quality, security scanning & automated deployment workflows",
    ],
    tags: ["GitLab CE", "Jenkins", "SonarQube", "CI/CD"],
  },
  {
    domain: "CI/CD · Platform Scale",
    title: "Jenkins Pipelines Across Dev, UAT & Production",
    bullets: [
      "Built and maintained 15+ Jenkins pipelines spanning dev, UAT, and production environments",
      "Branch-based GitLab triggers driving zero-downtime, rolling Docker deployments",
      "M365 SSO on pipeline access, with Python + Jenkins automating weekly SSL expiry reporting",
    ],
    tags: ["Jenkins", "GitLab CI/CD", "Docker", "Python", "M365 SSO"],
  },
  {
    domain: "DevSecOps · Organization-Wide Standard",
    title: "Shift-Left Security Pipeline",
    bullets: [
      "Rolled out a 4-layer security pipeline — Gitleaks, SonarQube, Trivy & Secrets Manager — across 5+ production applications",
      "Secrets Manager replaced plaintext .env files; ECR configured to reject CVE-flagged images",
      "Stage-by-stage MS Teams & email alerts across build, scan, violation & deploy phases",
    ],
    tags: ["Gitleaks", "SonarQube", "Trivy", "Secrets Manager", "ECR", "Jenkins"],
  },
  {
    domain: "Observability · Unified Monitoring",
    title: "Unified Monitoring Platform",
    bullets: [
      "Prometheus + Grafana dashboards with threshold-based email alerts for CPU, disk & memory",
      "Uptime Kuma tracking 40+ URLs and SSL certificates, with automated weekly expiry reports",
      "CloudWatch Logs for developer visibility, plus EventBridge + Lambda server-state notifications",
    ],
    tags: ["Prometheus", "Grafana", "Uptime Kuma", "CloudWatch", "EventBridge", "Lambda", "Python"],
  },
  {
    domain: "FinOps · Organization-Wide",
    title: "Foundational Cloud Cost-Optimization Practice",
    bullets: [
      "Established a foundational AWS cost-optimization practice through a granular organisational cost audit",
      "Built a repeatable framework — resource utilisation, right-sizing, storage optimisation, workload scheduling, Reserved Instances & Savings Plans — later applied across client environments",
    ],
    tags: ["Cost Explorer", "CUR", "EC2", "EventBridge", "EBS", "AWS Organizations"],
  },
];

const personalProjects = [
  {
    title: "Gaidhane Rice Mill — Static Site on AWS",
    description: "Vite + Tailwind CSS SPA with a custom vanilla-JS bilingual (English/Marathi) toggle, schema.org structured data for local SEO, and WhatsApp deep-link inquiries — built and deployed end-to-end as a static site on AWS S3 + CloudFront with GTM analytics.",
    ghLink: null,
    demoLink: "https://gaidhane.in/",
  },
  {
    title: "2048 Game on EKS with ALB & Ingress",
    description: "EKS deployment with VPC, ALB, and Ingress Controller — advanced Kubernetes networking and AWS integration.",
    ghLink: "https://github.com/Vedantgaidhane/Deploying-2048-Game-with-EKS-ALB-and-Ingress-Controller-in-a-VPC",
  },
  {
    title: "Wanderlust Three-Tier Deployment",
    description: "React + Node.js + MongoDB three-tier app deployed with Docker on AWS EC2 following multi-tier architecture best practices.",
    ghLink: "https://github.com/Vedantgaidhane/wanderlust",
  },
  {
    title: "CountryBank CI/CD Pipeline",
    description: "Jenkins pipeline with Docker, SonarQube, Trivy, and Gradle for automated build, security scan, and deployment on EC2.",
    ghLink: "https://github.com/Vedantgaidhane/CountryBank",
  },
  {
    title: "AWS Resource Tracker",
    description: "Shell script using AWS CLI + jq to display EC2, S3, IAM, and RDS resource details — automated cloud visibility tool.",
    ghLink: "https://github.com/Vedantgaidhane/AWS-Resource-Explorer",
  },
  {
    title: "AWS Node Health Tracker",
    description: "Bash script monitoring CPU, memory, and disk on AWS nodes — proactive detection of resource bottlenecks.",
    ghLink: "https://github.com/Vedantgaidhane/AWS-Node-Health-Tracker",
  },
  {
    title: "Lambda Cost Optimisation",
    description: "Scheduled Lambda function that removes unattached EC2 snapshots automatically — serverless cloud cost hygiene.",
    ghLink: null,
  },
];

function SectionLabel({ children, style = {} }) {
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: "0.9rem",
        fontSize: "1.25rem", fontWeight: 700, color: "var(--white)",
        margin: "2.5rem 0 0.35rem",
        ...style,
      }}
    >
      {children}
      <span style={{ flex: 1, height: 1, background: "linear-gradient(90deg, rgba(108,99,255,0.35), transparent)" }} />
    </div>
  );
}

function ClientCard({ p }) {
  return (
    <div className="client-card">
      <span className="client-domain-badge">{p.domain}</span>
      <div className="client-card-title">{p.title}</div>
      <ul className="client-card-bullets">
        {p.bullets.map((b) => <li key={b}>{b}</li>)}
      </ul>
      <div className="tech-tags">
        {p.tags.map((t) => <span key={t} className="tech-tag">{t}</span>)}
      </div>
    </div>
  );
}

function Projects() {
  return (
    <>
      <Helmet>
        <title>Projects | Vedant Gaidhane</title>
        <meta
          name="description"
          content="Production client engagements, platform initiatives, and personal projects by Vedant Gaidhane — AWS, FinOps, DevSecOps & cloud infrastructure."
        />
      </Helmet>

      <Container fluid className="project-section">
        <Particle />
        <Container style={{ position: "relative", zIndex: 2 }}>

          {/* Header */}
          <div style={{ paddingBottom: "0.5rem" }}>
            <h1 className="project-heading">
              My <strong className="purple">Work</strong>
            </h1>
            <p style={{ color: "var(--muted)", fontSize: "0.95rem" }}>
              Production engagements across FinTech, capital markets, insurance & non-profit — plus platform initiatives and personal projects
            </p>
          </div>

          {/* Client Engagements */}
          <SectionLabel>Client Engagements</SectionLabel>
          <p style={{ color: "var(--muted)", fontSize: "0.82rem", marginBottom: "1.25rem" }}>
            Live production work — infrastructure, DevSecOps & FinOps across multiple industries
          </p>
          <div className="client-cards-grid">
            {clientProjects.map((p) => <ClientCard key={p.title} p={p} />)}
          </div>

          {/* Initiatives */}
          <SectionLabel>Platform &amp; Organizational Initiatives</SectionLabel>
          <p style={{ color: "var(--muted)", fontSize: "0.82rem", marginBottom: "1.25rem" }}>
            Foundational infrastructure, DevOps platform engineering & FinOps practices built from the ground up
          </p>
          <div className="client-cards-grid">
            {internalProjects.map((p) => <ClientCard key={p.title} p={p} />)}
          </div>

          {/* Personal */}
          <SectionLabel>Personal Projects</SectionLabel>
          <p style={{ color: "var(--muted)", fontSize: "0.82rem", marginBottom: "1.25rem" }}>
            Hands-on learning — Kubernetes, CI/CD pipelines &amp; AWS automation
          </p>
          <Row style={{ justifyContent: "center" }}>
            {personalProjects.map((p) => (
              <Col key={p.title} md={4} className="project-card">
                <ProjectCard isBlog={false} title={p.title} description={p.description} ghLink={p.ghLink} demoLink={p.demoLink} />
              </Col>
            ))}
          </Row>

        </Container>
      </Container>
    </>
  );
}

export default Projects;
